import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const embryoBiopsyPgt: ContentBlock<"procedure"> = {
  id: makeId("proc-embryo-biopsy-pgt"),
  kind: "procedure",
  tags: ["ivf", "genomic", "lab", "both-partners"],
  title: "Embryo Biopsy & PGT",
  subtitle:
    "Removing cells from the embryo for genetic testing to select the healthiest for transfer",
  body: {
    procedureName: "Embryo Biopsy & PGT",
    category: "lab",
    description:
      "Removing a few cells from the embryo for genetic testing. Results help select the healthiest embryo for transfer.",
    howItWorks:
      "On Day 5-6, when the embryo has reached the blastocyst stage, an embryologist uses a laser to make a small opening in the outer shell and removes 5-10 cells from the trophectoderm (the part that becomes the placenta, not the baby). The cells are sent to a genetics lab while the embryo is frozen. PGT-A screens for chromosomal abnormalities; PGT-M tests for specific genetic conditions; PGT-SR tests for structural rearrangements. Results typically take 1-2 weeks.",
    duration: "Same day as Day 5-6 culture; results in 1-2 weeks",
    costRange: { low: 3000, high: 6000 },
    currency: "USD",
    whatToExpect: [
      "Embryo is biopsied on Day 5-6",
      "Embryo is frozen immediately after biopsy",
      "Results arrive in 1-2 weeks",
      "Results show which embryos are euploid (chromosomally normal)",
      "Your doctor will discuss results and recommend which embryo to transfer",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "PGT results usually return in 1-2 weeks after the biopsy sample reaches the genetics lab.",
      },
      {
        label: "What you learn",
        detail:
          "Reports usually classify embryos by chromosomal status. If PGT-M or PGT-SR was ordered, the report may include condition-specific findings.",
      },
      {
        label: "How it is used",
        detail:
          "Your doctor or genetic counselor should explain which embryos are recommended for transfer and whether any result needs clarification.",
      },
    ],
    risks: [
      "Very small risk of embryo damage during biopsy (<1%)",
      "Not all embryos may be suitable for biopsy",
      "PGT-A has a small false positive/negative rate",
    ],
    forWhom: "both",
    frBody: {
      procedureName: "Biopsie embryonnaire & DPI",
      description:
        "Prélèvement de quelques cellules de l'embryon pour analyse génétique. ⚠️ En France, seuls le DPI-M (maladies monogéniques graves) et le DPI-SR (remaniements structuraux) sont autorisés par la loi de bioéthique, sur validation d'un CPDPN. Le DPI-A (PGT-A) n'est pas autorisé en PMA française.",
      howItWorks:
        "À J5-J6, lorsque l'embryon a atteint le stade blastocyste, un embryologiste utilise un laser pour créer une petite ouverture dans la zone pellucide et prélève 5 à 10 cellules du trophectoderme (la partie qui deviendra le placenta, pas le bébé). Les cellules sont envoyées au laboratoire de génétique pendant que l'embryon est congelé. En France, le DPI (diagnostic préimplantatoire) est strictement encadré : DPI-M (PGT-M) pour maladies génétiques spécifiques et DPI-SR (PGT-SR) pour remaniements structuraux sont autorisés sur validation par un Centre Pluridisciplinaire de Diagnostic Prénatal (CPDPN) ; le DPI-A (PGT-A) n'est pas autorisé en routine. Les résultats sont disponibles sous 1 à 2 semaines.",
      duration:
        "Le jour même de la culture à J5-J6 ; résultats sous 1 à 2 semaines",
      whatToExpect: [
        "Biopsie réalisée à J5-J6",
        "Embryon congelé immédiatement après la biopsie",
        "Résultats sous 1 à 2 semaines",
        "Les résultats indiquent quels embryons sont euploïdes (chromosomiquement normaux)",
        "Votre médecin discutera des résultats et recommandera quel embryon transférer",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "Les résultats du DPI reviennent généralement sous 1 à 2 semaines après réception de l'échantillon par le laboratoire de génétique.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "Les rapports classent généralement les embryons selon leur statut chromosomique. Si un DPI-M ou DPI-SR a été demandé, le rapport peut inclure des résultats spécifiques.",
        },
        {
          label: "Comment c'est utilisé",
          detail:
            "Votre médecin ou conseiller en génétique doit expliquer quels embryons sont recommandés pour transfert et si un résultat doit être clarifié.",
        },
      ],
      risks: [
        "Risque très faible d'altération embryonnaire pendant la biopsie (<1 %)",
        "Tous les embryons ne sont pas éligibles à la biopsie",
        "Le DPI-A présente un faible taux de faux positifs/négatifs",
      ],
    },
  },
  sources: [
    {
      title: "Preimplantation genetic testing for aneuploidies (STAR trial)",
      authors: "Munné S et al.",
      journal: "Fertility and Sterility",
      year: 2019,
      url: "https://pubmed.ncbi.nlm.nih.gov/31551155/",
      evidenceLevel: "rct",
    },
    {
      title:
        "The use of preimplantation genetic testing for aneuploidy: a committee opinion",
      authors: "Practice Committees of ASRM and SART",
      journal: "Fertility and Sterility",
      year: 2024,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/the-use-of-preimplantation-genetic-testing-for-aneuploidy-a-committee-opinion-2024/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(embryoBiopsyPgt);

registerRelationship({
  from: embryoBiopsyPgt.id,
  to: makeId("test-preimplantation-genetic-testing"),
  type: "related-to",
  label: "Canonical test",
});
